/**
 * Every upcoming event, across every venue, in one table.
 *
 * Doug's ask, 23 September: the Venues screen already shows what a venue
 * *is* — its photo, its address, its sourcing — and "Coming up" on that
 * panel shows what one venue has next. Nothing showed what every venue has
 * next side by side, in the shape that lets a whole harvest be scanned at
 * once rather than clicked through one venue at a time. That panel grew its
 * own matching table the same day (`VenuePanel.tsx`) — see `eventFields.ts`
 * and `EventsTable.tsx`, split out once there were two readers of the same
 * columns rather than one.
 *
 * The `feed` view is one row per entry regardless of what kind of thing it
 * is — a concert and a film and an exhibition all have a `title` and a
 * `run_start`, but a concert's decision is made on its conductor and
 * programme, a film's on its director and ratings, an exhibition's on
 * nothing beyond its own dates. `expandedFields` (in `eventFields.ts`)
 * reads the columns that actually vary — `performers` (free-form, written
 * per adapter: conductor, ensemble, soloists, programme, director,
 * composer, language — see `toEntry` in `src/harvest/run.ts`) and `film`
 * (typed, written by `mergeFilmFacts`) — and prints whichever of them this
 * row actually has, the same "show what is really there" rule the Venues
 * panel's own Record section already follows for a venue.
 */
import { useEffect, useMemo, useState } from 'react'
import type { SupabaseClient } from '@supabase/supabase-js'
import { fetchAll } from '../../app/src/lib/fetchAll'
import { EVENT_FIELDS, typeLabel, type FeedEvent } from './eventFields'
import { EventsTable } from './EventsTable'

function useEvents(supabase: SupabaseClient) {
  const [events, setEvents] = useState<FeedEvent[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function refetch() {
    // `fetchAll`, not a plain `select` — the feed is past PostgREST's 1000-row
    // page already (1803 rows at last count) and a plain select would repeat
    // the exact silent truncation `fetchAll.ts`'s own note describes.
    // Ordered by `id`: the one thing `fetchAll`'s pagination can trust to be
    // stable, per that file's own rule. Sorting by date happens client-side.
    const { data, error } = await fetchAll<FeedEvent>(() =>
      supabase.from('feed').select(EVENT_FIELDS).order('id'))
    if (error) setError(error.message)
    else { setEvents(data); setError(null) }
  }

  useEffect(() => { void refetch() }, [])

  return { events, error, refetch }
}

type SortKey = 'soonest' | 'venue' | 'title'

export function EventsPanel({ supabase }: { supabase: SupabaseClient }) {
  const { events, error } = useEvents(supabase)
  const [q, setQ] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [sort, setSort] = useState<SortKey>('soonest')

  const types = useMemo(() => {
    const seen = new Set<string>()
    for (const e of events ?? []) seen.add(e.type)
    return [...seen].sort()
  }, [events])

  const needle = q.trim().toLowerCase()
  const filtered = useMemo(() => {
    let out = events ?? []
    if (needle) {
      out = out.filter(e =>
        e.title.toLowerCase().includes(needle) ||
        (e.venue ?? '').toLowerCase().includes(needle) ||
        (e.building ?? '').toLowerCase().includes(needle))
    }
    if (typeFilter !== 'all') out = out.filter(e => e.type === typeFilter)
    return out
  }, [events, needle, typeFilter])

  const sorted = useMemo(() => {
    const out = [...filtered]
    const dateKey = (e: FeedEvent) => e.run_start ?? e.published_on ?? '9999-12-31'
    if (sort === 'venue') out.sort((a, b) => (a.venue ?? a.building ?? '').localeCompare(b.venue ?? b.building ?? '') || dateKey(a).localeCompare(dateKey(b)))
    else if (sort === 'title') out.sort((a, b) => a.title.localeCompare(b.title))
    else out.sort((a, b) => dateKey(a).localeCompare(dateKey(b)))
    return out
  }, [filtered, sort])

  return (
    <div className="events">
      <div className="events-head">
        <input
          className="search"
          placeholder="Search a title or venue"
          value={q}
          onChange={e => setQ(e.currentTarget.value)}
        />
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="all">Every type</option>
          {types.map(t => <option key={t} value={t}>{typeLabel(t)}</option>)}
        </select>
        <select value={sort} onChange={e => setSort(e.target.value as SortKey)}>
          <option value="soonest">Soonest first</option>
          <option value="venue">Venue A–Z</option>
          <option value="title">Title A–Z</option>
        </select>
        {events && (
          <span className="dim events-count">
            {sorted.length}{sorted.length !== events.length ? ` of ${events.length}` : ''} upcoming
          </span>
        )}
      </div>

      {error && <p className="err" style={{ padding: '0 20px' }}>{error}</p>}
      {!error && !events && <p className="dim" style={{ padding: '0 20px' }}>Reading the feed…</p>}
      {!error && events && sorted.length === 0 && <p className="dim" style={{ padding: '0 20px' }}>Nothing matches.</p>}

      {!error && events && sorted.length > 0 && <EventsTable events={sorted} />}
    </div>
  )
}
